import type { FetchResult, Reference } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type {
  GetCareTeamQuery,
  GetCareTeamQueryVariables,
} from 'generated/care_team.generated';
import {
  GetCareTeamDocument,
  useDeleteCareTeamMemberMutation,
  useGetCareTeamQuery,
  useUpsertCareTeamMutation,
  type DeleteCareTeamMemberMutation,
  type DeleteCareTeamMemberMutationVariables,
  type UpsertCareTeamMutation,
  type UpsertCareTeamMutationVariables as _UpsertCareTeamMutationVariables,
} from 'generated/care_team.generated';
import { CareTeamCategory } from 'generated/types';

export const useGetCareTeam = (category?: CareTeamCategory) => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetCareTeamQuery({
    variables: {
      input: {
        patient_id,
        category: category ?? CareTeamCategory.Longitudinal,
      },
    },
    skip: !patient_id,
  });
  return { ...response, data: response.data?.getCareTeam };
};

export type UpsertCareTeamMutationVariables =
  _UpsertCareTeamMutationVariables & { category?: CareTeamCategory };
export function useUpsertCareTeam(): [
  (
    params: UpsertCareTeamMutationVariables
  ) => Promise<FetchResult<UpsertCareTeamMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpsertCareTeamMutation();
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'error',
  });
  const loading = baseLoading;

  async function upsertCareTeam(params: UpsertCareTeamMutationVariables) {
    const { patient_id } = params.input;
    const response = await mutate({
      variables: { ...params },
      onError: (e) => handleError(e.graphQLErrors),
      onCompleted: () => handleSuccess(),
      update: (cache, { data: response }) => {
        if (!response?.updateCareTeam) {
          return;
        }
        try {
          cache.writeQuery<GetCareTeamQuery, GetCareTeamQueryVariables>({
            query: GetCareTeamDocument,
            variables: {
              input: {
                patient_id,
                category: params.category ?? CareTeamCategory.Longitudinal,
              },
            },
            data: {
              getCareTeam: response.updateCareTeam,
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [upsertCareTeam, loading];
}

export function useDeleteCareTeamMember(): [
  (
    params: DeleteCareTeamMemberMutationVariables['input']
  ) => Promise<FetchResult<DeleteCareTeamMemberMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteCareTeamMemberMutation();
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'error',
  });
  const loading = baseLoading;

  async function deleteCareTeamMember(
    params: DeleteCareTeamMemberMutationVariables['input']
  ) {
    const response = await mutate({
      onError: (e) => handleError(e.graphQLErrors),
      onCompleted: () => handleSuccess(),
      variables: { input: { ...params } },
      update: (cache, { data: response }) => {
        if (!response?.deleteCareTeamMember) {
          return;
        }
        try {
          const memberId = params.member.replace(/^#/, '');
          const normalizedId = cache.identify({
            id: memberId,
            __typename: 'ContactModel',
          });

          // Remove participant ref from care team participants
          cache.modify<{ participants: Reference[] }>({
            id: cache.identify({
              patient_id: params.patient_id,
              category: CareTeamCategory.Longitudinal,
              __typename: 'PatientCareTeamModel',
            }),
            fields: {
              participants(existing) {
                return existing.filter((a) => a.__ref !== normalizedId);
              },
            },
          });

          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteCareTeamMember, loading];
}
